import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { useNavigate } from "react-router-dom";
import Table from "../../../../Components/UiComponents/Table/Table";
import { XCircleblack } from "../../../../Components/IconComponents";
import "./MyIndividualTaskList.css";
import { facilityTableData } from "../../../../JsonData/CreateFacilityConfig";
import TaskModalWithComment from "../../../../Components/UiComponents/TaskModalWithComment/TaskModalWithComment";
import { postDataWithBody } from "../../../../Services/Services";
import {
  getAllIndlTask,
  completeNotificationTypeTask,
  completeIndivTask,
} from "../../../../Services/EndPoints";
import Moment from "react-moment";
import { toast, ToastContainer } from "react-toastify";
import Confirmpopup from "../../Common/ConfirmPopup/Confirmpopup";
import { useKeycloak } from "@react-keycloak/web";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "react-bootstrap";

/**
 * MyIndividualTaskList Component
 * Displays a table of individual tasks with status management and modal interactions
 * Supports both notification and activity type tasks
 */
const MyIndividualTaskList = forwardRef((props, ref) => {
  // Core state management
  const [data, setData] = useState([]); // Task list data
  const [rowCount, setRowCount] = useState(0); // Total number of tasks
  const [taskData, setTaskData] = useState(null); // Selected task for modal
  const [showTaskModal, setShowTaskModal] = useState(false); // Task completion modal visibility

  // Hooks
  const { t } = useTranslation();
  const { keycloak } = useKeycloak();

  // Fetch tasks when user is authenticated
  useEffect(() => {
    if (keycloak.authenticated) {
      fetchTaskList();
    }
  }, [keycloak.authenticated]);

  /**
   * Fetches the task list from the API
   */
  const fetchTaskList = async () => {
    try {
      const response = await postDataWithBody(
        getAllIndlTask,
        { email: getLoggedInUserEmail() },
        {}
      );
      
      if (response) {
        setRowCount(response.length);
        setData(response);
      }
    } catch (error) {
      console.error("Error fetching task list:", error);
      toast.error("Failed to fetch tasks", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  /**
   * Expose refresh function to parent component
   */
  useImperativeHandle(ref, () => ({
    refreshTaskList: fetchTaskList
  }));

  /**
   * Get logged in user's email from Keycloak token
   */
  const getLoggedInUserEmail = () => {
    return keycloak.idTokenParsed?.email || '';
  };

  /**
   * Show task completion modal for activity tasks
   */
  const showTaskCompletionModal = (taskItem) => {
    setTaskData(taskItem);
    setShowTaskModal(true);
  };

  /**
   * Refresh table and close modal after task completion
   */
  const handleTaskCompletion = () => {
    fetchTaskList();
    setShowTaskModal(false);
  };

  /**
   * Component for displaying truncated text with modal popup
   */
  const TruncatedTextCell = ({ text, title, maxLength = 25 }) => {
    const [showModal, setShowModal] = useState(false);
    const [isHighlighted, setIsHighlighted] = useState(false);

    const displayText = text || "NA";
    const truncatedText = displayText.length > maxLength 
      ? `${displayText.substring(0, maxLength)}...` 
      : displayText;

    const handleShowModal = () => {
      setIsHighlighted(true);
      setShowModal(true);
    };

    const handleCloseModal = () => {
      setShowModal(false);
      setIsHighlighted(false);
    };

    // Apply custom modal styling
    useEffect(() => {
      if (showModal) {
        const modalElement = document.querySelector(".modal-content");
        if (modalElement) {
          modalElement.style.cssText = 
            "background-color: #1c2038 !important; border-radius: 20px !important;";
        }
      }
    }, [showModal]);

    return (
      <span>
        <span
          title={displayText}
          style={{
            cursor: "pointer",
            backgroundColor: isHighlighted ? "#1a7a73" : "transparent",
            padding: "2px 5px",
            borderRadius: "5px",
          }}
          onClick={handleShowModal}
        >
          {truncatedText}
        </span>

        <Modal
          show={showModal}
          onHide={handleCloseModal}
          dialogClassName="custom-modal"
          centered
          style={{ borderRadius: "20px", border: "none", top: "20%" }}
        >
          <Modal.Header
            style={{
              backgroundColor: "#1a7a73",
              color: "white",
              borderBottom: "none",
            }}
          >
            <Modal.Title style={{ color: "white", marginRight: "200px" }}>
              {title}
            </Modal.Title>
            <XCircleblack
              onClick={handleCloseModal}
              width="1.5em"
              height="1.5em"
              className="cursor-pointer"
            />
          </Modal.Header>
          <Modal.Body
            style={{
              backgroundColor: "#fcfcfc",
              color: "#001b30",
              border: "none",
              padding: "20px",
              lineHeight: "1.8",
            }}
          >
            <p>{displayText}</p>
          </Modal.Body>
          <Modal.Footer
            style={{
              backgroundColor: "#fcfcfc",
              borderTop: "none",
            }}
          >
            <Button
              variant="secondary"
              onClick={handleCloseModal}
              style={{
                backgroundColor: "#1a7a73",
                borderColor: "#003366",
                borderRadius: "5px",
              }}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </span>
    );
  };

  /**
   * Component for handling task status changes
   */
  const TaskStatusCell = ({ row }) => {
    const [showNotificationConfirm, setShowNotificationConfirm] = useState(false);
    const [showActivityConfirm, setShowActivityConfirm] = useState(false);
    
    const { task_status_code: status, task_type, id } = row.original;

    /**
     * Handle status change selection
     */
    const handleStatusChange = (newStatus) => {
      if (newStatus === "TASK_EXE_STATUS_INPROGRESS") {
        if (task_type === 0) { // Activity task
          showTaskCompletionModal(row.original);
        } else { // Notification task
          setShowNotificationConfirm(true);
        }
      } else if (newStatus === "TASK_EXE_STATUS_COMPLETED") {
        if (task_type === 1) { // Notification task
          setShowNotificationConfirm(true);
        } else { // Activity task
          showTaskCompletionModal(row.original);
        }
      }
    };

    /**
     * Complete notification task
     */
    const completeNotificationTask = async () => {
      try {
        const response = await postDataWithBody(
          completeNotificationTypeTask,
          { id, email: getLoggedInUserEmail() },
          {}
        );
        
        if (response.status === "success") {
          fetchTaskList();
          toast.success(response.msg, {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
          });
        }
      } catch (error) {
        console.error("Error completing notification task:", error);
        toast.error("Failed to complete task");
      }
      setShowNotificationConfirm(false);
    };

    /**
     * Complete activity task
     */
    const completeActivityTask = async () => {
      try {
        const todayDate = new Date().toISOString().split("T")[0];
        const response = await postDataWithBody(
          completeIndivTask,
          { id, taskCompletedDate: todayDate },
          {}
        );
        
        if (response.status === "success") {
          fetchTaskList();
          toast.success(response.msg, {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
          });
        }
      } catch (error) {
        console.error("Error completing activity task:", error);
        toast.error("Failed to complete task");
      }
      setShowActivityConfirm(false);
    };

    /**
     * Get dropdown background color based on status
     */
    const getStatusColor = (statusCode) => {
      const colors = {
        "TASK_EXE_STATUS_NEW_TASK": "#f0a4ac",
        "TASK_EXE_STATUS_INPROGRESS": "#fccd7c",
        "TASK_EXE_STATUS_COMPLETED": "#7cdb90",
      };
      return colors[statusCode] || "gray";
    };

    // Dropdown styling
    const selectStyle = {
      appearance: "none",
      WebkitAppearance: "none",
      MozAppearance: "none",
      padding: "5px 20px 5px 5px",
      border: "1px solid #ddd",
      borderRadius: "4px",
      backgroundColor: getStatusColor(status),
      color: "black",
      width: "120px",
      cursor: "pointer",
    };

    const wrapperStyle = {
      position: "relative",
      display: "inline-block"
    };

    const arrowStyle = {
      position: "absolute",
      right: "8px",
      top: "50%",
      transform: "translateY(-50%)",
      width: "0",
      height: "0",
      borderLeft: "5px solid transparent",
      borderRight: "5px solid transparent",
      borderTop: "5px solid black",
      pointerEvents: "none"
    };

    // Handle special case: task started by someone else
    if (status === "TASK_EXE_STATUS_INPROGRESS_SOMEONE") {
      return (
        <div className="startedbysomeone">
          <div>{t("Task started by someone")}</div>
        </div>
      );
    }

    return (
      <div>
        <div style={wrapperStyle}>
          <select
            value={status}
            onChange={(e) => handleStatusChange(e.target.value)}
            style={selectStyle}
          >
            <option
              value="TASK_EXE_STATUS_NEW_TASK"
              disabled={status !== "TASK_EXE_STATUS_NEW_TASK"}
            >
              {task_type === 1 ? t("New Message") : t("New Task")}
            </option>
            
            {task_type !== 1 && (
              <option
                value="TASK_EXE_STATUS_INPROGRESS"
                disabled={status === "TASK_EXE_STATUS_COMPLETED"}
              >
                {t("In Progress")}
              </option>
            )}
            
            <option
              value="TASK_EXE_STATUS_COMPLETED"
              disabled={
                task_type === 1
                  ? status !== "TASK_EXE_STATUS_NEW_TASK"
                  : status !== "TASK_EXE_STATUS_INPROGRESS"
              }
            >
              {task_type === 1 ? t("Acknowledged") : t("Completed")}
            </option>
          </select>
          <div style={arrowStyle}></div>
        </div>

        {/* Confirmation popups */}
        {showNotificationConfirm && (
          <Confirmpopup
            show={showNotificationConfirm}
            title={t("acknowldgtask")}
            message={t("popupmsg")}
            handleToggle={() => setShowNotificationConfirm(false)}
            row={row}
            name={row.original.task_name}
            deleteRow={completeNotificationTask}
            cancel={t("cancelbutton")}
            action={t("confirm")}
          />
        )}

        {showActivityConfirm && (
          <Confirmpopup
            show={showActivityConfirm}
            title={t("acknowldgtask")}
            message="Are you sure you want to complete the task?"
            handleToggle={() => setShowActivityConfirm(false)}
            row={row}
            name={row.original.task_name}
            deleteRow={completeActivityTask}
            cancel={t("cancelbutton")}
            action={t("confirm")}
          />
        )}
      </div>
    );
  };

  /**
   * Table column definitions
   */
  const columns = React.useMemo(
    () => [
      {
        Header: t("dailyoperationaltasktableheaders.taskname"),
        accessor: "name",
        disableSortBy: true,
        Cell: ({ value }) => (
          <span title={value}>{value || "NA"}</span>
        ),
      },
      {
        Header: t("dailyoperationaltasktableheaders.description"),
        accessor: "description",
        disableSortBy: true,
        Cell: ({ value }) => (
          <TruncatedTextCell 
            text={value} 
            title="Full Description" 
          />
        ),
      },
      {
        Header: t("dailyoperationaltasktableheaders.actionmessage"),
        accessor: "action_message",
        disableSortBy: true,
        Cell: ({ value }) => (
          <TruncatedTextCell 
            text={value} 
            title="Full Action Message" 
          />
        ),
      },
      {
        Header: t("dailyoperationaltasktableheaders.createdby"),
        accessor: "created_by",
        disableSortBy: true,
      },
      {
        Header: t("dailyoperationaltasktableheaders.tasktype"),
        accessor: "task_type",
        disableSortBy: true,
        Cell: ({ value }) => {
          return value === 1 ? "Notification" : "Activity";
        },
      },
      {
        Header: t("dailyoperationaltasktableheaders.createdon"),
        accessor: "created_on",
        Cell: ({ value }) => {
          return value ? (
            <Moment format="DD-MM-YYYY, hh:mm A">{value}</Moment>
          ) : "NA";
        },
        disableSortBy: true,
      },
      {
        Header: t("dailyoperationaltasktableheaders.updatedon"),
        accessor: "updated_on",
        Cell: ({ value }) => {
          return value ? (
            <Moment format="DD-MM-YYYY, hh:mm A">{value}</Moment>
          ) : "NA";
        },
        disableSortBy: true,
      },
      {
        Header: t("dailyoperationaltasktableheaders.status"),
        accessor: "task_status_code",
        disableSortBy: true,
        Cell: ({ row }) => <TaskStatusCell row={row} />,
      },
    ],
    [t]
  );

  return (
    <div>
      {/* Task completion modal */}
      {showTaskModal && (
        <TaskModalWithComment
          titleValue={t("actiontaken")}
          showSuccessMsg={handleTaskCompletion}
          taskData={taskData}
          show={showTaskModal}
          buttonLabel={t("popup.save")}
          handleToggle={showTaskCompletionModal}
          getMyTaskList={fetchTaskList}
          setShowPop={setShowTaskModal}
          {...props}
        />
      )}

      {/* Main task table */}
      {facilityTableData && (
        <Table
          data={data}
          title=""
          columns={columns}
          numberOfRows={7}
          showExport={false}
          showReset={false}
          onExpand={false}
          showSearch={true}
          isHeaderFixed={true}
          rowCount={rowCount}
          headerTableHeadStyle={{ 
            background: "#1a7a73", 
            color: "#FFFFFF" 
          }}
          tableStyle={{
            border: "1px solid #f0f0f0",
            padding: "2px",
            borderRadius: "12px",
          }}
        />
      )}

      {/* Toast notifications */}
      <ToastContainer />
    </div>
  );
});

export default MyIndividualTaskList;